// Hints for the AutoJobs "Confirm mapped fields" panel: the raw address text (what the AI read from the file) is compared with the fields the
// parser filled. Hints only -- they never block anything. The first rule does not depend on any country's address format, so it also catches
// mistakes nobody has thought of yet; the other two name the two mistakes already seen.
export interface HintFields {
  address_line_1: string; address_line_2: string; address_line_3: string; address_line_4: string
  city: string; county: string; postcode: string; country: string   // country = ISO-2 or ''
}
export interface AddressHint { id: 'unmapped' | 'postcode_shape' | 'city_street'; message: string }

const tokens = (s: string): string[] => (s.toUpperCase().match(/[A-Z0-9]+/g) ?? [])
const UK_POSTCODE = /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i
// Road words that are never part of a city name. (ST and DR are left out on purpose: "ST ALBANS".)
const STREET_WORDS = new Set(['ROAD', 'RD', 'STREET', 'LANE', 'AVENUE', 'AVE', 'BOULEVARD', 'BLVD', 'HIGHWAY'])

export function addressMappingHints(raw: string | null | undefined, f: HintFields, resolveCountry: (text: string) => string | null): AddressHint[] {
  const text = (raw ?? '').trim()
  if (!text) return []
  const hints: AddressHint[] = []

  // 1. Words of the original text that ended up in NO field (compared without case and punctuation). The country is matched by meaning
  //    ("CHINA" is covered when Country = CN), not by spelling.
  const mapped = new Set(tokens([f.address_line_1, f.address_line_2, f.address_line_3, f.address_line_4, f.city, f.county, f.postcode].join(' ')))
  const rawTokens = tokens(text)
  const coveredByCountry = new Set<number>()
  if (f.country) {
    for (let i = 0; i < rawTokens.length; i++) {
      if (resolveCountry(rawTokens[i]) === f.country) coveredByCountry.add(i)
      if (i + 1 < rawTokens.length && resolveCountry(`${rawTokens[i]} ${rawTokens[i + 1]}`) === f.country) { coveredByCountry.add(i); coveredByCountry.add(i + 1) }
    }
  }
  const lost: string[] = []
  rawTokens.forEach((t, i) => {
    if (mapped.has(t) || coveredByCountry.has(i) || (t.length < 2 && !/\d/.test(t)) || lost.includes(t)) return
    lost.push(t)
  })
  if (lost.length > 0) hints.push({ id: 'unmapped', message: `The text has ${lost.length === 1 ? 'a word' : 'words'} that ${lost.length === 1 ? 'is' : 'are'} in none of the fields: ${lost.slice(0, 8).join(', ')}. Check the text on the left.` })

  // 2. A postcode-shaped value in the text, but Postcode is empty (6 digits: China and others; or a UK postcode).
  if (!f.postcode.trim()) {
    const six = rawTokens.find(t => /^\d{6}$/.test(t))
    const uk = text.match(UK_POSTCODE)
    const found = six ?? (uk ? uk[0].toUpperCase() : null)
    if (found) hints.push({ id: 'postcode_shape', message: `The text contains ${found}, which looks like a postcode, but Postcode is empty.` })
  }

  // 3. City holds a road word.
  const cityWord = tokens(f.city).find(t => STREET_WORDS.has(t))
  if (cityWord) hints.push({ id: 'city_street', message: `City “${f.city.trim()}” looks like a street name (${cityWord}), not a city.` })

  return hints
}
